import React from "react";
import { Link } from "react-router-dom";
import { QrCode, FileText, Wrench, ShieldCheck, ScanLine, Hammer } from "lucide-react";

const HOW_IT_WORKS = [
  {
    icon: Hammer,
    title: "Embossed & engraved",
    body: "Hard-wearing engraved nameplates — board designation up top, QR code below. Mechanically fixed for industrial durability.",
  },
  {
    icon: ScanLine,
    title: "Scan in seconds",
    body: "Any phone camera reads the QR — no app, no login. Instant access on-site for engineers, contractors and auditors.",
  },
  {
    icon: FileText,
    title: "Latest EICR + schedule",
    body: "The QR opens the current board schedule and the latest EICR certificate for that specific board — always up to date.",
  },
  {
    icon: Wrench,
    title: "Live modification log",
    body: "Electricians making add-on circuits can log changes against the board record — keeping the schedule accurate over time.",
  },
];

const QRNameplate = () => {
  return (
    <section
      className="relative mt-24 lg:mt-32"
      id="qr-nameplate"
      data-testid="qr-nameplate-section"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="eyebrow">Smarter Compliance · Lutrom Smart Nameplate</p>
            <h2
              className="font-display mt-5 text-white"
              style={{ fontSize: "clamp(36px, 5vw, 60px)", lineHeight: 0.95 }}
            >
              EVERY BOARD WE TOUCH
              <br />
              <span className="gradient-blue-text">GETS A QR-CODED NAMEPLATE.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
              Following an electrical installation or an EICR, every distribution
              board we work on is fitted with a Lutrom engraved nameplate —
              mechanically attached for industrial durability. Scan its QR code on
              any phone and the latest board schedule and EICR certificate appear
              instantly. No filing cabinets. No "where's the cert?". Just compliance,
              on tap.
            </p>
          </div>
          <Link
            to="/services#electrical-installation"
            className="btn-ghost inline-flex w-fit items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold"
            data-testid="qr-nameplate-service-link"
          >
            See electrical services
          </Link>
        </div>

        {/* Image #1 + features card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div
            className="lg:col-span-7 rounded-2xl overflow-hidden service-card p-0 relative"
            data-testid="qr-nameplate-image-board-wrap"
          >
            <img
              src="/qr-nameplate/nameplate-board.jpg"
              alt="Schneider Acti9 distribution board with a yellow Lutrom QR nameplate reading 'DB1 — Fed From DB18/1TP — Scan For DB Details', mechanically attached and ready to scan"
              className="w-full block"
              style={{ aspectRatio: "4 / 5", objectFit: "cover", objectPosition: "center top" }}
              loading="lazy"
              data-testid="qr-nameplate-image-board"
            />
            <div
              className="absolute inset-x-0 bottom-0 p-6 lg:p-8"
              style={{
                background:
                  "linear-gradient(180deg, rgba(6,11,24,0) 0%, rgba(6,11,24,0.85) 100%)",
              }}
            >
              <p className="eyebrow" style={{ color: "rgba(230,236,255,0.85)" }}>
                On the board
              </p>
              <p className="mt-2 text-sm text-white font-semibold">
                Engraved board designation &amp; QR — mechanically fixed for life.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 glow-card rounded-2xl p-6 lg:p-8">
            <span className="icon-tile">
              <QrCode size={22} />
            </span>
            <h3
              className="font-display mt-5 text-white"
              style={{ fontSize: "clamp(22px, 2.6vw, 30px)", lineHeight: 1.02 }}
            >
              ONE SCAN. EVERYTHING YOU NEED.
            </h3>
            <p className="mt-4 text-sm text-[color:rgba(230,236,255,0.85)] leading-relaxed">
              No app. No login. No paperwork hunt. The nameplate is a portable,
              tamper-resistant link between the physical board and its live digital
              record — owned by you, maintained by Lutrom.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { v: "0s", l: "Cert lookup time" },
                { v: "100%", l: "Boards fitted" },
                { v: "Any", l: "Phone — no app" },
                { v: "Live", l: "Modification log" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-lg p-3"
                  style={{
                    background: "rgba(6, 11, 24, 0.45)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <p
                    className="font-display text-white"
                    style={{ fontSize: 22, lineHeight: 1 }}
                  >
                    {s.v}
                  </p>
                  <p className="mt-1.5 text-[10.5px] uppercase tracking-widest text-[color:rgba(230,236,255,0.7)]">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How it works strip */}
        <div className="mt-5">
          <div className="glass-card rounded-2xl p-6 lg:p-10">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
              <div>
                <p className="eyebrow">How it works</p>
                <h3
                  className="font-display mt-4 text-white"
                  style={{ fontSize: "clamp(22px, 2.8vw, 34px)", lineHeight: 1.02 }}
                >
                  FROM NAMEPLATE
                  <span className="gradient-blue-text"> TO LIVE RECORD.</span>
                </h3>
              </div>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {HOW_IT_WORKS.map((s) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.title}
                    className="rounded-xl p-5"
                    style={{
                      background: "rgba(6, 11, 24, 0.5)",
                      border: "1px solid var(--lt-border)",
                    }}
                    data-testid={`qr-nameplate-step-${s.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  >
                    <span
                      className="icon-tile"
                      style={{ width: 42, height: 42 }}
                    >
                      <Icon size={18} />
                    </span>
                    <p
                      className="font-display text-white mt-5"
                      style={{ fontSize: 17, letterSpacing: "0.02em" }}
                    >
                      {s.title.toUpperCase()}
                    </p>
                    <p className="mt-2 text-[13px] text-[color:var(--lt-text-dim)] leading-relaxed">
                      {s.body}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
            data-testid="qr-nameplate-cta-contact"
          >
            <ShieldCheck size={15} /> Get smart nameplates on your boards
          </Link>
        </div>
      </div>
    </section>
  );
};

export default QRNameplate;
